<template>
  <v-table class="deposits-table">
    <thead v-if="headers">
      <tr>
        <th>Name</th>
        <th>Accession Number</th>
        <th>Identifier</th>
        <th>ArchivesSpace Accession ID</th>
        <th>ArchivesSpace Archival Object ID</th>
        <th>Collecting Area</th>
        <th>Created date</th>
        <th>Status</th>
        <th>Actions</th>
        <th v-if="enableEdit && isAdmin">
          Edit
        </th>
        <th v-if="isAdmin">
          Delete
        </th>
        <th v-if="isAdmin">
          Submitted for ingest
        </th>
      </tr>
    </thead>

    <tbody>
      <template
        v-for="(deposit, idx) in deposits"
        :key="deposit.id"
      >
        <!-- Group header row (appears at first row and whenever status changes) -->
        <tr
          v-if="groupByStatus && (idx === 0 || deposits[idx - 1].status !== deposit.status)"
          class="section-header-row"
        >
          <td :colspan="columnCount">
            <strong>{{ deposit.status === 'open' ? 'Open deposits' : 'Closed deposits' }}</strong>
            <span
              v-if="deposit.status !== 'open'"
              class="muted"
            >
              These deposits have been closed and submitted for ingest, so no new files can be added to them.
            </span>
          </td>
        </tr>

        <tr>
          <td class="truncate">
            <router-link
              title="Access the deposit area"
              :to="{
                name: 'DepositFileHome',
                params: { deposit_id: deposit.id },
                query: {
                  depositor: deposit.depositor?.['custom:identityid'],
                  deposit_name: deposit.name,
                  ...(adminUserEmail ? {
                    admin_user: adminUserEmail,
                    ...(adminUserName ? { admin_user_name: adminUserName } : {}),
                    ...(adminSearch ? { admin_search: adminSearch } : {}),
                    ...(adminPage > 1 ? { admin_page: adminPage } : {}),
                  } : {})
                }
              }"
            >
              {{ deposit.name || "—" }}
            </router-link>
          </td>

          <td class="truncate">
            <!-- Normal case: accessionNumber present -->
            <template v-if="deposit.accessionNumber">
              {{ deposit.accessionNumber }}
            </template>

            <!-- Fallback preview: show externalIdentifier in red with tooltip -->
            <template v-else-if="deposit.externalIdentifier && deposit.externalIdentifier.trim() !== ''">
              <span
                class="fallback-inline"
                role="text"
              >
                <span class="fallback-red fallback-number">
                  {{ deposit.externalIdentifier }}
                </span>

                <v-tooltip
                  text="Please open and re-save this Deposit before closing to correctly store this value."
                  location="top"
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-icon
                      v-bind="props"
                      size="16"
                      class="fallback-icon"
                    >
                      mdi-information-outline
                    </v-icon>
                  </template>
                </v-tooltip>
              </span>
            </template>
            <template v-else>
              —
            </template>
          </td>
          <td class="truncate">
            {{ deposit.identifier || "—" }}
          </td>
          <td class="truncate">
            {{ deposit.accessionId || "—" }}
          </td>
          <td class="truncate">
            {{ deposit.archivesSpaceArchivalObjectRecordId || "—" }}
          </td>
          <td class="truncate">
            {{ deposit.collectingArea || "-" }}
          </td>
          <td class="truncate">
            {{ formatDate(deposit.createdDate) || "—" }}
          </td>
          <td
            v-if="deposit.status === 'open'"
            class="truncate"
          >
            <span class="open-text">Open</span>
          </td>
          <td
            v-else-if="deposit.status === 'closed'"
            class="truncate"
          >
            <span class="closed-text">Closed</span>
          </td>

          <td class="long-button">
            <!-- Open: Close & Submit -->
            <v-btn
              v-if="deposit.status === 'open'"
              block
              :disabled="!isAdmin || isCloseBlocked(deposit)"
              :title="isCloseBlocked(deposit)
                ? 'Please open and re-save this Deposit before closing to correctly store this value.'
                : 'Close & Submit'"
              @click="askClose(deposit)"
            >
              <span class="btn-text">Close &amp; Submit</span>
            </v-btn>

            <!-- Closed, NOT yet submitted: can reopen -->
            <v-btn
              v-else-if="deposit.status === 'closed' && !deposit.lastSubmittedForIngest"
              block
              :disabled="!isAdmin"
              @click="toggleDeposit(deposit.id)"
            >
              Reopen
            </v-btn>

            <!-- Closed AND has lastSubmittedForIngest: locked => RESUBMIT -->
            <v-btn
              v-else-if="deposit.status === 'closed' && deposit.lastSubmittedForIngest"
              block
              :disabled="!isAdmin"
              @click="askResubmit(deposit)"
            >
              Resubmit
            </v-btn>
          </td>

          <td v-if="enableEdit && isAdmin">
            <v-btn
              icon
              variant="text"
              :title="`Edit ${deposit.name}`"
              :disabled="disableEditWhenClosed && deposit.status !== 'open'"
              @click="openEdit(deposit)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
          </td>

          <td v-if="isAdmin">
            <v-btn
              v-if="canDelete(deposit)"
              icon
              variant="text"
              color="error"
              :title="`Delete ${deposit.name || 'this deposit'}`"
              @click="askDelete(deposit)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </td>

          <td
            v-if="deposit.lastSubmittedForIngest != null && isAdmin"
            class="truncate"
          >
            {{ formatDate(deposit.lastSubmittedForIngest) }}
            <v-chip
              v-if="ingestStatusOf(deposit)"
              class="ingest-status"
              size="small"
              variant="tonal"
              :color="ingestStatusOf(deposit).color"
              :prepend-icon="ingestStatusOf(deposit).icon"
              :append-icon="workbenchUrlOf(deposit) ? 'mdi-open-in-new' : undefined"
              :href="workbenchUrlOf(deposit) ?? undefined"
              :target="workbenchUrlOf(deposit) ? '_blank' : undefined"
              :rel="workbenchUrlOf(deposit) ? 'noopener noreferrer' : undefined"
              :title="ingestStatusTitle(deposit)"
            >
              {{ ingestStatusOf(deposit).label }}
            </v-chip>
          </td>
          <td
            v-else-if="deposit.lastSubmittedForIngest == null && isAdmin"
            class="truncate"
          >
            —
          </td>
        </tr>
      </template>
    </tbody>
  </v-table>

  <!-- Edit dialog -->
  <v-dialog
    v-model="editDialog"
    max-width="960"
    @update:model-value="onEditDialogToggle"
  >
    <v-card>
      <v-card-title class="text-h6">
        Edit deposit details
      </v-card-title>

      <v-card-text>
        <v-form
          ref="editForm"
          v-model="editFormValid"
          lazy-validation
          class="form-narrow"
        >
          <!-- Name -->
          <div class="field w-80">
            <div class="label-row">
              <span
                id="lbl-name"
                class="form-label"
              >Name</span>
              <v-tooltip
                text="E.g. 'CUL Records'. This will be the 'Title' field of the package when this Deposit is ingested into the repository."
                content-class="tip-wrap-100"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="info-icon"
                    icon
                    size="x-small"
                    variant="text"
                    :ripple="false"
                    :aria-label="'Help: Name'"
                  >
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>

            <v-text-field
              id="name"
              v-model="editableDeposit.name"
              :aria-labelledby="'lbl-name'"
              placeholder="Enter name"
              :rules="nameRules"
              :counter="250"
              clearable
              variant="outlined"
            />
          </div>

          <!-- Collecting Area -->
          <div class="field w-80">
            <div class="label-row">
              <span
                id="lbl-colarea"
                class="form-label"
              >Collecting Area</span>
              <v-tooltip
                text="The Collecting Area this Deposit will be assigned to when ingested into the repository."
                content-class="tip-wrap-100"
              >
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="info-icon"
                    icon
                    size="x-small"
                    variant="text"
                    :ripple="false"
                    :aria-label="'Help: Collecting Area'"
                  >
                    <v-icon>mdi-information-outline</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </div>

            <v-select
              id="collectingArea"
              v-model="editableDeposit.collectingArea"
              :aria-labelledby="'lbl-colarea'"
              :items="collectingAreaOptions"
              placeholder="Select collecting area"
              :rules="collectingAreaRules"
              clearable
              variant="outlined"
            />
          </div>

          <!-- Pair 1: Accession Number OR Identifier -->
          <div class="pair-row">
            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-accessionNumber"
                  class="form-label"
                >Accession Number</span>
                <v-tooltip
                  text="E.g. 'GBR/0265/2025/032'. This will be the 'Accession Number' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Accession Number'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="accessionNumber"
                v-model="editableDeposit.accessionNumber"
                :aria-labelledby="'lbl-accessionNumber'"
                class="pair-left"
                placeholder="Enter accession number"
                :rules="externalIdRules"
                :counter="200"
                clearable
                variant="outlined"
              />
            </div>

            <div
              class="or-chip"
              aria-hidden="true"
            >
              OR
            </div>

            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-identifier"
                  class="form-label"
                >Identifier</span>
                <v-tooltip
                  text="E.g. 'GBR/0012/MS SPR/107'. This will be the 'Identifier' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: Identifier'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="identifier"
                v-model="editableDeposit.identifier"
                :aria-labelledby="'lbl-identifier'"
                class="pair-right"
                placeholder="Enter identifier"
                :rules="identifierRules"
                :counter="200"
                clearable
                variant="outlined"
              />
            </div>
          </div>

          <!-- Pair 2: ArchivesSpace Accession ID OR ArchivesSpace Archival Object ID -->
          <div class="pair-row">
            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-accessionId"
                  class="form-label"
                >ArchivesSpace Accession ID</span>
                <v-tooltip
                  text="E.g. '27510'. You can find this number at the end of the URL to the accession record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/accessions/27510. This will be the 'ID of ArchivesSpace Accession Record' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: ArchivesSpace Accession ID'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="accessionId"
                v-model="editableDeposit.accessionId"
                :aria-labelledby="'lbl-accessionId'"
                class="pair-left"
                placeholder="Digits only"
                :rules="accessionRules"
                :counter="200"
                clearable
                type="text"
                inputmode="numeric"
                pattern="[0-9]*"
                variant="outlined"
              />
            </div>

            <div
              class="or-chip"
              aria-hidden="true"
            >
              OR
            </div>

            <div class="field">
              <div class="label-row">
                <span
                  id="lbl-aaoid"
                  class="form-label"
                >ArchivesSpace Archival Object ID</span>
                <v-tooltip
                  text="E.g. '1728103'. You can find this number at the end of the URL to the record in ArchivesSpace, e.g. https://arcspace-staff.lib.cam.ac.uk/resources/13400#tree::archival_object_1728103. This will be the 'ID of ArchivesSpace Archival Object Record' field of the package when this Deposit is ingested into the repository."
                  content-class="tip-wrap-100"
                >
                  <template #activator="{ props }">
                    <v-btn
                      v-bind="props"
                      class="info-icon"
                      icon
                      size="x-small"
                      variant="text"
                      :ripple="false"
                      :aria-label="'Help: ArchivesSpace Archival Object ID'"
                    >
                      <v-icon>mdi-information-outline</v-icon>
                    </v-btn>
                  </template>
                </v-tooltip>
              </div>

              <v-text-field
                id="archivesSpaceArchivalObjectRecordId"
                v-model="editableDeposit.archivesSpaceArchivalObjectRecordId"
                :aria-labelledby="'lbl-aaoid'"
                class="pair-right"
                placeholder="Enter Archival Object ID"
                :rules="aaoidRules"
                :counter="200"
                clearable
                variant="outlined"
              />
            </div>
          </div>
        </v-form>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          @click="editDialog = false"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          :loading="saving"
          :disabled="!canSave"
          @click="saveEdit"
        >
          Save
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="confirmCloseOpen"
    persistent
    max-width="660"
  >
    <v-card class="confirmCard">
      <v-card-title class="text-h6">
        Are you sure?
      </v-card-title>

      <v-card-text>
        <p>
          Closing <strong>{{ confirmTarget?.name || 'this deposit' }}</strong> will prevent the depositor from adding further files
          and immediately submit the deposit for ingest into the DPS.
        </p>
      </v-card-text>

      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          :disabled="confirmBusy"
          @click="cancelClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          :loading="confirmBusy"
          @click="confirmClose"
        >
          Close & Submit
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="confirmResubmitOpen"
    persistent
    max-width="660"
  >
    <v-card class="confirmCard">
      <v-card-title class="text-h6">
        Are you sure?
      </v-card-title>
      <v-card-text>
        <p class="warn-line">
          Resubmitting this deposit for ingest will create a duplicate package in the DPS
          if it has already been ingested before.
        </p>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          :disabled="confirmBusy"
          @click="cancelClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="secondary"
          :loading="confirmBusy"
          @click="confirmResubmit"
        >
          Resubmit anyway
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>

  <v-dialog
    v-model="confirmDeleteOpen"
    persistent
    max-width="660"
  >
    <v-card class="confirmCard">
      <v-card-title class="text-h6">
        Are you sure?
      </v-card-title>
      <v-card-text>
        <p>
          Deleting <strong>{{ confirmTarget?.name || 'this deposit' }}</strong> will permanently remove it.
          This cannot be undone.
        </p>
        <p
          v-if="deleteError"
          class="delete-error"
        >
          {{ deleteError }}
        </p>
      </v-card-text>
      <v-card-actions class="justify-end">
        <v-btn
          variant="text"
          :disabled="confirmBusy"
          @click="cancelClose"
        >
          Cancel
        </v-btn>
        <v-btn
          color="error"
          :loading="confirmBusy"
          :disabled="!!deleteError"
          @click="confirmDelete"
        >
          Delete
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import config from "@/config";
import { Auth } from "@/utils/amplify-compat";
import { S3Client, DeleteObjectCommand } from "@aws-sdk/client-s3";
import { formatDate } from "@/utils/formatDate";
import { ingestStatusOf, workbenchUrlOf } from "@/utils/ingestStatus";
import { clearIngestStatus, updateDepositMetadata } from "@/utils/depositMetadata";
import { ensureAdmin, isAdminFromToken } from '@/utils/adminTools'
import { depositHasFiles, identityIdFromDepositKey } from '@/utils/depositFiles'

export default {
  name: "DepositTable",
  props: {
    deposits: { type: Array, default: () => [] },
    headers: Boolean,
    enableEdit: { type: Boolean, default: true },
    disableEditWhenClosed: { type: Boolean, default: true },
    groupByStatus: { type: Boolean, default: false },
    adminUserEmail: { type: String, default: '' },
    adminUserName: { type: String, default: '' },
    adminSearch: { type: String, default: '' },
    adminPage: { type: Number, default: 1 },
    // ids of open deposits with no uploaded files
    emptyDepositIds: { type: Array, default: () => [] },
  },
  emits: ['refresh'],
  data() {
    return {
      // editing state
      allowSaving: false,
      confirmCloseOpen: false,
      confirmResubmitOpen: false,
      confirmDeleteOpen: false,
      deleteError: '',
      confirmTarget: null,
      confirmBusy: false,
      editDialog: false,
      collectingAreaOptions: [
        'Cambridge University Digital Library',
        'Cultural Heritage Imaging Laboratory',
        'Department of Archives and Modern Manuscripts',
        'Digital Preservation',
        'Map Department',
        'Music Department',
        'Open Research Systems',
        'World Collections',
      ],
      editableDeposit: {
        id: null,
        key: null,
        name: "",
        accessionNumber: "",
        accessionId: "",
        identifier: "",
        archivesSpaceArchivalObjectRecordId: "",
        lastSubmittedForIngest: "",
        collectingArea: "Department of Archives and Modern Manuscripts",
      },
      isAdmin: false,
      originalSnapshot: null,
      rowRef: null,
      editFormValid: true,
      saving: false,

      // simple rules
      nameRules: [
        v => (v === null || v === undefined || v === '' || v.length <= 250) || 'Name must be <= 250 chars'
      ],
    };
  },
  computed: {
    // number of columns, so group header rows span the whole table
    columnCount() {
      // Name to Actions, then the admin-only Edit, Delete and Submitted for ingest
      return 9 + (this.enableEdit && this.isAdmin ? 1 : 0) + (this.isAdmin ? 2 : 0);
    },
    // allow overriding save state in cases where only externalIdentifier is present
    canSave() {
    // form must be valid AND (changes OR explicit allowSaving)
    return this.isAdmin && this.editFormValid && (this.isDirty || this.allowSaving) && !this.saving;
    },
    // disable Save unless something actually changed
    isDirty() {
      if (!this.originalSnapshot) return false;
      return (
        (this.editableDeposit.name ?? '') !== (this.originalSnapshot.name ?? '') ||
        (this.editableDeposit.identifier ?? '') !== (this.originalSnapshot.identifier ?? '') ||
        (this.editableDeposit.accessionNumber ?? '') !== (this.originalSnapshot.accessionNumber ?? '') ||
        (this.editableDeposit.accessionId ?? '') !== (this.originalSnapshot.accessionId ?? '') ||
        (this.editableDeposit.archivesSpaceArchivalObjectRecordId ?? '') !== (this.originalSnapshot.archivesSpaceArchivalObjectRecordId ?? '') ||
        (this.editableDeposit.collectingArea ?? '') !== (this.originalSnapshot.collectingArea ?? '') ||
        (this.lastSubmittedForIngest ?? '') !== (this.originalSnapshot.lastSubmittedForIngest ?? '')
      );
    },

    /* === XOR rules (exactly one) ===
       Pair 1: accessionNumber XOR identifier
       Pair 2: accessionId (numeric when present) XOR archivesSpaceArchivalObjectRecordId
    */
    identifierRules() {
      return [
        v => (v === null || v === undefined || v === '' || String(v).length <= 200) || 'Identifier must be <= 200 chars',
        () => this.accessionVsIdentifierRule() || true,
      ];
    },
    externalIdRules() {
      return [
        v => (v === null || v === undefined || v === '' || String(v).length <= 200) || 'Accession Number must be <= 200 chars',
        () => this.accessionVsIdentifierRule() || true,
      ];
    },
    accessionRules() {
      return [
        v => (v === null || v === undefined || v === '' || /^\d+$/.test(String(v))) || 'Accession ID must be numeric',
        () => this.aspaceAccessionVsAOidRule() || true,
      ];
    },
    aaoidRules() {
      return [
        v => (v === null || v === undefined || v === '' || String(v).length <= 200) || 'Archival Object ID must be <= 200 chars',
        () => this.aspaceAccessionVsAOidRule() || true,
      ];
    },
    collectingAreaRules() {
      return [
        v => (v === '' || v === null || v === undefined || this.collectingAreaOptions.includes(v)) || 'Must be one of the listed options',
      ];
    },
  },
  watch: {
    'editableDeposit.accessionNumber'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.identifier'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.accessionId'() { this.$refs.editForm?.validate?.(); },
    'editableDeposit.archivesSpaceArchivalObjectRecordId'() { this.$refs.editForm?.validate?.(); },
  },
  async mounted() {
    try {
      if (this.$cognitoAuth?.getIdToken) {
        this.isAdmin = await ensureAdmin(this.$cognitoAuth, '105877');
      } else {
        const session = await Auth.currentSession();
        const token = session?.getIdToken?.()?.getJwtToken?.();
        this.isAdmin = !!token && isAdminFromToken(token, '105877');
      }
    } catch {
      this.isAdmin = false;
    }
  },
  methods: {
    hasExternalOnly(deposit) {
      const acc = (deposit?.accessionNumber ?? '').trim();
      const ext = (deposit?.externalIdentifier ?? '').trim();
      return !acc && !!ext;
    },
    isCloseBlocked(deposit) {
      return deposit?.status === 'open' && this.hasExternalOnly(deposit);
    },
    
    askClose(deposit) {
      if (!this.isAdmin) return;
      if (this.isCloseBlocked(deposit)) return;
      this.confirmTarget = deposit;
      this.confirmCloseOpen = true;
    },

    cancelClose() {
      this.confirmBusy = false;
      this.confirmCloseOpen = false;
      this.confirmResubmitOpen = false;
      this.confirmDeleteOpen = false;
      this.deleteError = '';
      this.confirmTarget = null;
    },

    async confirmClose() {
      if (!this.confirmTarget || this.isCloseBlocked(this.confirmTarget)) return;
      this.confirmBusy = true;
      try {
        // sets status=closed and stamps lastSubmittedForIngest
        if (!(await this.toggleDeposit(this.confirmTarget.id))) throw new Error('S3 write failed');
        this.cancelClose();
      } catch (e) {
        console.warn('Failed to close deposit', e);
        this.confirmBusy = false;
      }
    },

    askResubmit(deposit) {
      if (!this.isAdmin) return;
      this.confirmTarget = deposit;
      this.confirmResubmitOpen = true;
    },

    async confirmResubmit() {
      if (!this.confirmTarget) return;
      this.confirmBusy = true;
      try {
        await this.resubmitDeposit(this.confirmTarget.id);
        this.cancelClose(); // closes dialog + clears target
      } catch (e) {
        console.warn('Failed to resubmit deposit', e);
        this.confirmBusy = false; // keep dialog open so user can retry/cancel
      }
    },

    canDelete(deposit) {
      return this.isAdmin && deposit?.status === 'open' && this.emptyDepositIds.includes(deposit.id);
    },

    askDelete(deposit) {
      if (!this.canDelete(deposit)) return;
      this.confirmTarget = deposit;
      this.deleteError = '';
      this.confirmDeleteOpen = true;
    },

    async confirmDelete() {
      const deposit = this.confirmTarget;
      if (!this.canDelete(deposit)) return;
      this.confirmBusy = true;
      try {
        const credentials = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(credentials),
          forcePathStyle: true,
        });

        // Re-check in case files were uploaded since the list was loaded
        const identityId = identityIdFromDepositKey(deposit.key);
        if (!identityId || await depositHasFiles(client, identityId, deposit.id)) {
          this.deleteError = 'Files have been uploaded to this deposit, so it can no longer be deleted.';
          this.confirmBusy = false;
          this.$emit('refresh');
          return;
        }

        await client.send(new DeleteObjectCommand({ Bucket: config.bucketName, Key: deposit.key }));
        this.cancelClose();
        this.$emit('refresh');
      } catch (e) {
        console.warn('Failed to delete deposit', e);
        this.deleteError = 'Unable to delete this deposit. Please try again.';
        this.confirmBusy = false;
      }
    },

    async resubmitDeposit(metadataId) {
      if (!this.isAdmin) return;
      const row = this.deposits.find(d => d.id === metadataId);
      if (!row) return;

      const metadata = await this.updateMetadata(row.key, (metadata) => {
        // status remains CLOSED; just update lastSubmittedForIngest
        metadata.status = 'closed';
        metadata.lastSubmittedForIngest = new Date().toISOString();
        clearIngestStatus(metadata);
      });
      if (!metadata) throw new Error('S3 write failed');

      this.syncRow(row, metadata);
    },

    coalesceAccessionNumber(a, e) {
      const A = (a ?? '').trim();
      const E = (e ?? '').trim();

      if (A == '' && E != '') {
        this.allowSaving = true;
      }

      return A || E || '';
    },
    formatDate,
    ingestStatusOf,
    workbenchUrlOf,

    ingestStatusTitle(deposit) {
      const parts = [];
      if (this.workbenchUrlOf(deposit)) parts.push('View this package in the Workbench');
      if (deposit.ingestStatusLastUpdated) {
        parts.push(`Ingest status updated ${formatDate(deposit.ingestStatusLastUpdated)}`);
      }
      return parts.join('. ') || null;
    },

    getActionLabel(deposit) {
      return deposit?.lastSubmittedForIngest ? 'Resubmit' : 'Close & Submit';
    },
    getActionIcon(deposit) {
      // keep your archive icon, or pick a “resubmit” style icon for resubmits
      return deposit?.lastSubmittedForIngest ? 'mdi-reload' : 'mdi-archive';
    },

    // Applies `update` to the current metadata file (see updateDepositMetadata).
    // Returns the written metadata, or null if aborted or the write failed.
    async updateMetadata(objectKey, update) {
      try {
        const credentials = await Auth.currentCredentials();
        const client = new S3Client({
          region: config.region,
          credentials: Auth.essentialCredentials(credentials),
        });

        return await updateDepositMetadata(client, objectKey, update);
      } catch (err) {
        console.log("Error:", err);
        return null;
      }
    },

    // Bring a row in line with what was written, including any changes made
    // since the page loaded (e.g. ingestStatus)
    syncRow(row, metadata) {
      for (const field of Object.keys(row)) {
        if (field !== 'key' && !(field in metadata)) delete row[field];
      }
      Object.assign(row, metadata);
    },

    // ---- XOR helpers ----
    isBlank(v) {
      return v === null || v === undefined || String(v).trim() === '';
    },
    xorMessage(aVal, bVal, aLabel, bLabel) {
      const aBlank = this.isBlank(aVal);
      const bBlank = this.isBlank(bVal);
      if (aBlank && bBlank) return `Provide ${aLabel} OR ${bLabel}.`;
      if (!aBlank && !bBlank) return `Provide only one of ${aLabel} OR ${bLabel}, not both.`;
      return ''; // exactly one provided -> ok
    },
    accessionVsIdentifierRule() {
      return this.xorMessage(
        this.editableDeposit.accessionNumber,
        this.editableDeposit.identifier,
        'Accession Number',
        'Identifier'
      );
    },
    aspaceAccessionVsAOidRule() {
      return this.xorMessage(
        this.editableDeposit.accessionId,
        this.editableDeposit.archivesSpaceArchivalObjectRecordId,
        'AS Accession ID',
        'AS Archival Object ID'
      );
    },

    isSectionHeader(idx) {
      if (!this.groupByStatus) return false;
      const curr = this.deposits[idx]?.status ?? '';
      const prev = this.deposits[idx - 1]?.status ?? '';
      // Show a section header at the first row OR whenever status changes.
      return idx === 0 || curr !== prev;
    },

    sectionTitleFor(status) {
      return status === 'open' ? 'Open deposits' : 'Closed deposits';
    },

    // Returns true if the deposit's status was changed
    async toggleDeposit(metadataId) {
      if (!this.isAdmin) return false;
      const row = this.deposits.find((d) => d.id === metadataId);
      if (!row) return false;

      const metadata = await this.updateMetadata(row.key, (metadata) => {
        switch (metadata.status) {
          case "closed":
            // If it’s "locked" (already submitted), do NOT allow reopen
            if (metadata.lastSubmittedForIngest) {
              console.warn('Deposit is locked (already submitted); cannot reopen.');
              return false;
            }
            metadata.status = "open";
            break;

          case "open":
            // Closing always sets lastSubmittedForIngest (first submission)
            metadata.status = "closed";
            metadata.lastSubmittedForIngest = new Date().toISOString();
            clearIngestStatus(metadata);
            break;

          default:
            console.log(`Unknown status ${metadata.status} in deposit`);
            return false;
        }
      });
      if (!metadata) return false;

      this.syncRow(row, metadata);
      return true;
    },

    openEdit(deposit) {
      if (!this.isAdmin) return;
      this.allowSaving = false;
      this.rowRef = deposit;
      this.editableDeposit = {
        id: deposit.id,
        key: deposit.key,
        name: deposit.name ?? "",
        identifier: deposit.identifier ?? "",
        accessionNumber: this.coalesceAccessionNumber(deposit.accessionNumber, deposit.externalIdentifier),
        archivesSpaceArchivalObjectRecordId: deposit.archivesSpaceArchivalObjectRecordId ?? "",
        accessionId: deposit.accessionId ?? "",
        collectingArea: deposit.collectingArea ?? "Department of Archives and Modern Manuscripts",
      };
      this.originalSnapshot = { 
          ...this.editableDeposit,
          collectingArea: deposit.collectingArea ?? "",   // raw value, not the display default — lets isDirty catch a first-time set
      };
      this.$nextTick(() => this.$refs.editForm?.resetValidation?.());
      this.editDialog = true;
    },

    // ✨ reset state when dialog closes
    onEditDialogToggle(val) {
      if (!val) {
        this.allowSaving = false;
        this.rowRef = null;
        this.originalSnapshot = null;
        this.editableDeposit = {
          id: null,
          key: null,
          name: "",
          accessionNumber: "",
          accessionId: "",
          identifier: "",
          archivesSpaceArchivalObjectRecordId: "",
          collectingArea: "Department of Archives and Modern Manuscripts",
        };
        this.$refs.editForm?.resetValidation?.();
      }
    },

    // ✨ validate, persist to S3, update the row optimistically
    async saveEdit() {
      if (!this.isAdmin) return;
      // run Vuetify validators
      if (!this.$refs.editForm?.validate?.()) return;

      // extra XOR safety
      const err1 = this.xorMessage(
        this.editableDeposit.accessionNumber,
        this.editableDeposit.identifier,
        'Accession Number',
        'Identifier'
      );
      const err2 = this.xorMessage(
        this.editableDeposit.accessionId,
        this.editableDeposit.archivesSpaceArchivalObjectRecordId,
        'AS Accession ID',
        'AS Archival Object ID'
      );
      if (err1 || err2) {
        this.$refs.editForm?.validate?.();
        return;
      }

      if (!this.rowRef) return;

      // On save, trust ONLY what is in the form.
      // If the field is empty, we treat it as “no accession number”.
      const preferredAccession = (this.editableDeposit.accessionNumber ?? '').trim();

      this.saving = true;
      const metadata = await this.updateMetadata(this.rowRef.key, (metadata) => {
        metadata.name = this.editableDeposit.name ?? "";
        metadata.accessionNumber = preferredAccession;
        metadata.identifier = this.editableDeposit.identifier ?? "";
        metadata.archivesSpaceArchivalObjectRecordId = this.editableDeposit.archivesSpaceArchivalObjectRecordId ?? "";
        metadata.accessionId = this.editableDeposit.accessionId ?? "";
        metadata.collectingArea = this.editableDeposit.collectingArea ?? "";

        // We are wiping out the externalIdentifier at this point
        delete metadata.externalIdentifier;
      });
      this.allowSaving = false;  // Ensure any override from coalesceAccessionNumber is reset.
      this.saving = false;

      if (metadata) {
        // keep UI in sync, including dropping externalIdentifier from the row
        this.syncRow(this.rowRef, metadata);

        this.editDialog = false;
        this.$emit('refresh'); // If you want to re-fetch from S3 instead
      } else {
        console.warn("Failed to save updated metadata to S3");
      }
    },
  },
};
</script>

<style scoped>
/* ====== Table cell layout (original) ====== */
.truncate {
  min-width: 11%;
  max-width: 11%;
  justify-content: center;
}

/* lock action button widths so columns stay aligned */
:deep(td .v-btn) {
  min-width: 7rem;   /* or 10ch / 120px */
  max-width: 7rem;
  justify-content: center;
}

/* tighter icon-only buttons (e.g. Edit/Delete) */
:deep(td .v-btn.v-btn--icon) {
  min-width: 2.5rem;
  max-width: 2.5rem;
  justify-content: center;
}

/* Ensure the table fills the parent width */
:deep(.v-table__wrapper > table) {
  width: 100%;
  table-layout: fixed;
}

/* Allow content to wrap instead of forcing horizontal scroll */
:deep(.v-table__wrapper th),
:deep(.v-table__wrapper td) {
  white-space: normal;
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* If your container sits inside a dialog/card with padding you don't want */
:deep(.v-card-text),
:deep(.v-card) {
  padding-left: 0 !important;
  padding-right: 0 !important;
}

/* ====== Form width/centering in dialog (original) ====== */
v-dialog input {
  width: 80%;
}

.form-narrow :deep(.v-input) {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}

/* On small screens, let them be full width */
@media (max-width: 600px) {
  .form-narrow :deep(.v-input) {
    width: 100%;
  }
}

/* ====== New: External labels with info (“i”) tooltip ====== */
.label-row {
  display: inline-flex;
  align-items: center;
  justify-content: center;   /* center the label + info icon */
  gap: 0.4rem;
  margin: 0.25rem auto;
  width: 100%;
  text-align: center;        /* helps if label wraps to multiple lines */
}

.form-label {
  font-weight: 600;
  line-height: 1.2;
  white-space: normal;
  overflow: visible;
  text-overflow: unset;
  /* no explicit width so it shrinks to content and centers nicely with the icon */
}

/* small grey info icon */
.info-icon :deep(.v-icon) {
  opacity: 0.7;
}
.info-icon:hover :deep(.v-icon),
.info-icon:focus-visible :deep(.v-icon) {
  opacity: 1;
}

/* ====== New: Pair layout + centered “OR” chip ====== */
.pair-row {
  display: grid;
  grid-template-columns: 1fr auto 1fr; /* left | OR | right */
  gap: 0.75rem;
  align-items: start;
  width: 80%;
  margin: 0.75rem auto 0;
}

.w-80 {
  width: 80%;
  margin-left: auto;
  margin-right: auto;
}

.pair-left,
.pair-right {
  width: 100%;
}

/* “OR” pill */
.or-chip {
  align-self: center;
  font-weight: 700;
  font-size: 0.75rem;
  line-height: 1;
  padding: 0.4rem 0.6rem;
  border: 1px solid rgba(0,0,0,0.2);
  border-radius: 999px;
  user-select: none;
  opacity: 0.75;
}

/* Stack fields and relax widths on smaller screens */
@media (max-width: 800px) {
  .pair-row {
    grid-template-columns: 1fr;
    width: 100%;
  }
  .w-80 {
    width: 100%;
  }
  .or-chip {
    display: none; /* optional — show if you want it always visible */
  }
}

/* Wrap long tooltip text around ~100 characters */
:deep(.tip-wrap-100) {
  max-width: 100ch !important;          /* ≈ 100 characters */
  white-space: normal !important;        /* allow wrapping */
  overflow-wrap: anywhere;    /* break long words/URLs if needed */
  word-break: normal;
  text-align: left;           /* long text is easier to read left-aligned */
  line-height: 1.25;
}

/* Keep tooltips sensible on small screens */
@media (max-width: 600px) {
  :deep(.tip-wrap-100) { max-width: 90vw; }
}

.fallback-inline {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  white-space: nowrap;
  min-width: 0;
  max-width: 100%;
}

/* Make long IDs truncate nicely instead of wrapping or pushing the icon down */
.fallback-number {
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
  word-break: keep-all !important;
  overflow-wrap: normal !important;
}

.fallback-red {
  color: #c62828;
  font-weight: 600;
}

.fallback-icon {
  flex: 0 0 auto;
  vertical-align: text-bottom;
  opacity: 0.9;
  margin-left: 0.1rem;
}
.fallback-icon:hover,
.fallback-icon:focus-visible {
  opacity: 1;
}

.open-text {
  color: green;
}
.closed-text {
  color: red;
}
:deep(td.long-button .v-btn) {
  height: auto;
  min-height: 2.5rem;
}

:deep(td.long-button .v-btn .v-btn__content) {
  white-space: normal !important;
  flex-wrap: nowrap;
  gap: 0.35rem;
  justify-content: center;
}

:deep(td.long-button .btn-text) {
  flex: 1 1 auto;
  min-width: 0;
  white-space: normal !important;
  overflow-wrap: anywhere;
  word-break: break-word;
  text-align: center;
}

/* Keep the icon fixed-size at the right */
:deep(td.long-button .v-btn .v-icon) {
  flex: 0 0 auto;
  align-self: center;
}

.confirmCard {
  text-align: center;
  padding: 5px;
}

.delete-error {
  color: #c62828;
  margin-top: 0.5rem;
}

.ingest-status {
  margin-top: 0.25rem;
}
</style>
